import { useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Sparkles, Calculator, ArrowLeft } from "lucide-react";
import { Button } from "@/common/components/ui/button";
import { Card, CardContent } from "@/common/components/ui/card";
import { CompactCalculatorRow } from "../CompactCalculatorRow";
import { calculateRetirement, formatCurrency } from "../../utils/calculations";
import ProductLayout from "@/common/components/ProductLayout";

export const RetirementCalculator = () => {
  const navigate = useNavigate();
  const [retCurrentAge, setRetCurrentAge] = useState(30);
  const [retRetireAge, setRetRetireAge] = useState(60);
  const [retExpectancy] = useState(85);
  const [retExpenses, setRetExpenses] = useState(30000);
  const [retInflation, setRetInflation] = useState(6);
  const [retPreRate, setRetPreRate] = useState(12);
  const [retPostRate, setRetPostRate] = useState(8);

  const retResult = useMemo(
    () =>
      calculateRetirement(
        retCurrentAge,
        retRetireAge,
        retExpectancy,
        retExpenses,
        retInflation,
        retPreRate,
        retPostRate
      ),
    [
      retCurrentAge,
      retRetireAge,
      retExpectancy,
      retExpenses,
      retInflation,
      retPreRate,
      retPostRate,
    ]
  );

  return (
    <ProductLayout>
      <div className="min-h-screen bg-background py-8">
        <div className="container mx-auto px-4 md:px-6">
          <div className="mb-8">
            <Button
              variant="ghost"
              onClick={() => navigate("/calculators")}
              className="mb-4 pl-0 hover:bg-transparent hover:text-primary"
            >
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back to Calculators
            </Button>
            <div className="flex items-center gap-4">
              <div className="rounded-lg bg-primary/10 p-3 text-primary">
                <Calculator className="h-8 w-8" />
              </div>
              <h1 className="text-3xl font-bold text-secondary">
                Retirement Planning
              </h1>
            </div>
          </div>

          <div className="mx-auto max-w-7xl">
            <div className="grid gap-10 lg:grid-cols-2">
              <Card className="border-border bg-card">
                <CardContent className="space-y-6 p-6 md:p-8">
                  <div className="mb-2 flex items-center gap-3">
                    <div className="rounded-xl bg-purple-100 p-3">
                      <Sparkles className="h-6 w-6 text-purple-600" />
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-secondary">
                        Retirement Planning
                      </h3>
                      <p className="text-sm text-muted-foreground">
                        Plan your freedom
                      </p>
                    </div>
                  </div>
                  <div className="space-y-6">
                    <div className="grid grid-cols-2 gap-4">
                      <CompactCalculatorRow
                        label="Current Age"
                        value={retCurrentAge}
                        onChange={setRetCurrentAge}
                        min={18}
                        max={60}
                        step={1}
                        suffix="Yrs"
                        colorClass="text-secondary"
                        hideSlider
                      />
                      <CompactCalculatorRow
                        label="Retirement Age"
                        value={retRetireAge}
                        onChange={setRetRetireAge}
                        min={40}
                        max={75}
                        step={1}
                        suffix="Yrs"
                        colorClass="text-secondary"
                        hideSlider
                      />
                    </div>

                    <CompactCalculatorRow
                      label="Monthly Expenses (Today)"
                      value={retExpenses}
                      onChange={setRetExpenses}
                      min={10000}
                      max={500000}
                      step={1000}
                      prefix="₹"
                      colorClass="text-primary"
                    />

                    <CompactCalculatorRow
                      label="Inflation Rate"
                      value={retInflation}
                      onChange={setRetInflation}
                      min={1}
                      max={12}
                      step={0.5}
                      suffix="%"
                      colorClass="text-red-600"
                    />

                    <div className="grid grid-cols-2 gap-4">
                      <CompactCalculatorRow
                        label="Pre-Retire Return"
                        value={retPreRate}
                        onChange={setRetPreRate}
                        min={1}
                        max={30}
                        step={0.5}
                        suffix="%"
                        colorClass="text-green-600"
                        hideSlider
                      />
                      <CompactCalculatorRow
                        label="Post-Retire Return"
                        value={retPostRate}
                        onChange={setRetPostRate}
                        min={1}
                        max={20}
                        step={0.5}
                        suffix="%"
                        colorClass="text-blue-600"
                        hideSlider
                      />
                    </div>
                  </div>
                </CardContent>
              </Card>
              <Card className="border-border bg-muted/20">
                <CardContent className="p-8">
                  <div className="mb-8 text-center">
                    <p className="mb-1 text-xs font-bold uppercase tracking-wider text-muted-foreground">
                      Required Corpus
                    </p>
                    <h2 className="text-4xl font-black text-purple-700">
                      {formatCurrency(retResult.corpus)}
                    </h2>
                  </div>
                  <div className="space-y-6">
                    <div className="rounded-xl border border-slate-100 bg-white p-4 shadow-sm">
                      <p className="mb-2 text-center font-medium text-slate-500">
                        Monthly SIP Required
                      </p>
                      <p className="text-center text-3xl font-black text-primary">
                        {formatCurrency(retResult.monthlySIP)}
                      </p>
                    </div>
                    <div className="space-y-2 text-sm text-slate-600">
                      <div className="flex justify-between">
                        <span>Expense Value at Retirement:</span>
                        <span className="font-bold">
                          {formatCurrency(retResult.expenseAtRetire)} / month
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span>Years to Accumulate:</span>
                        <span className="font-bold">
                          {retRetireAge - retCurrentAge} years
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span>Life Expectancy:</span>
                        <span className="font-bold">{retExpectancy} years</span>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </div>
    </ProductLayout>
  );
};
